import type { BrandFields } from "@/components/admin/companyProfile/BrandingSection";
import type { Brand, BrandExtraction, BrandKit } from "@/types";

/** Flat form state for the brand editor: identity + branding-kit fields. */
export interface BrandFormState extends BrandFields {
  name: string;
  description: string;
  audience: string;
  tags: string;
}

export function emptyBrandForm(): BrandFormState {
  return {
    name: "",
    description: "",
    audience: "",
    tags: "",
    brand_primary: "",
    brand_secondary: "",
    brand_accents: "",
    brand_background: "",
    brand_text: "",
    brand_font_heading: "",
    brand_font_body: "",
    brand_tone: "",
    brand_style: "",
  };
}

/** Populate the flat form from a loaded Brand (edit mode). */
export function brandToForm(brand: Brand): BrandFormState {
  const palette = brand.brandKit?.palette ?? null;
  const fonts = brand.brandKit?.fonts ?? null;
  return {
    name: brand.name,
    description: brand.description ?? "",
    audience: brand.audience ?? "",
    tags: (brand.tags ?? []).join(", "),
    brand_primary: palette?.primary ?? "",
    brand_secondary: palette?.secondary ?? "",
    brand_accents: (palette?.accents ?? []).join(", "),
    brand_background: palette?.background ?? "",
    brand_text: palette?.text ?? "",
    brand_font_heading: fonts?.heading ?? "",
    brand_font_body: fonts?.body ?? "",
    brand_tone: brand.brandKit?.tone ?? "",
    brand_style: brand.brandStyle ?? "",
  };
}

/** Build a structured BrandKit from flat form fields, or null when all empty. */
export function formToBrandKit(form: BrandFormState): BrandKit | null {
  const accents = _splitList(form.brand_accents);
  const palette = {
    primary: form.brand_primary || null,
    secondary: form.brand_secondary || null,
    accents: accents.length > 0 ? accents : null,
    background: form.brand_background || null,
    text: form.brand_text || null,
  };
  const fonts = {
    heading: form.brand_font_heading || null,
    body: form.brand_font_body || null,
  };
  const hasPalette = Object.values(palette).some((v) => v !== null);
  const hasFonts = Object.values(fonts).some((v) => v !== null);
  const tone = form.brand_tone || null;
  if (!hasPalette && !hasFonts && !tone) return null;
  return {
    palette: hasPalette ? palette : null,
    fonts: hasFonts ? fonts : null,
    tone,
  };
}

/** Parse the comma-separated tags field into a string array (or null). */
export function formTags(form: BrandFormState): string[] | null {
  const tags = _splitList(form.tags);
  return tags.length > 0 ? tags : null;
}

/** Populate the flat form from an AI-extracted draft, preserving existing identity fields. */
export function extractionToForm(draft: BrandExtraction, current: BrandFormState): BrandFormState {
  const palette = draft.brandKit?.palette ?? null;
  const fonts = draft.brandKit?.fonts ?? null;
  return {
    ...current,
    // Identity fields only fill when still empty (never clobber a typed name).
    name: current.name || (draft.name ?? ""),
    description: current.description || (draft.description ?? ""),
    audience: current.audience || (draft.audience ?? ""),
    // Tokens overwrite with whatever the AI found ("" clears when absent).
    brand_primary: palette?.primary ?? "",
    brand_secondary: palette?.secondary ?? "",
    brand_accents: (palette?.accents ?? []).join(", "),
    brand_background: palette?.background ?? "",
    brand_text: palette?.text ?? "",
    brand_font_heading: fonts?.heading ?? "",
    brand_font_body: fonts?.body ?? "",
    brand_tone: draft.brandKit?.tone ?? "",
    brand_style: current.brand_style || (draft.guidance ?? ""),
  };
}

function _splitList(value: string): string[] {
  return value
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);
}
